import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MoreHorizontal, Eye, KeyRound, UserCheck, UserX, ShieldOff, Copy } from 'lucide-react';
import { copyToClipboard } from '@/lib/utils';
import {
  useUsers,
  useSendPasswordReset,
  useUpdateUserStatus,
  useResetTwoFactor,
} from '@/hooks/api/useUsers';
import { useDebounce, useUrlTable } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead, SortOrder } from '@/components/ui/sortable-table-head';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import Routes from '@/router/routes';
import { formatDate } from '@/lib/date';

interface TableState {
  page: number;
  limit: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string;
  emailVerified?: string;
}

export default function SuperAdminUsers() {
  const navigate = useNavigate();
  const { state, setPage, setSearch, setSort, setFilter } = useUrlTable<TableState>({
    page: 1,
    limit: 25,
    search: '',
    sortBy: '',
    sortOrder: 'desc',
    status: '',
    emailVerified: '',
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
    confirmLabel?: string;
    action?: 'resetPassword' | 'toggleStatus' | 'reset2fa';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  const debouncedSearch = useDebounce(state.search, 300);

  const handleSort = (sortBy: string, sortOrder: SortOrder) => {
    setSort(sortBy, sortOrder);
  };

  const { data, isLoading, isError, refetch } = useUsers({
    page: state.page,
    limit: state.limit,
    search: debouncedSearch || undefined,
    sortBy: (state.sortBy || undefined) as
      | 'name'
      | 'email'
      | 'createdAt'
      | 'organizationCount'
      | undefined,
    sortOrder: state.sortOrder,
    status: (state.status || undefined) as 'active' | 'deactivated' | 'pending' | 'all' | undefined,
    emailVerified:
      state.emailVerified === 'true' ? true : state.emailVerified === 'false' ? false : undefined,
  });

  const { mutate: sendPasswordReset, isPending: isSendingReset } = useSendPasswordReset();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateUserStatus();
  const { mutate: resetTwoFactor, isPending: isResetting2FA } = useResetTwoFactor();

  const handleRowClick = (userId: string) => {
    navigate(`${Routes.super_admin_dashboard_users.path}/${userId}`);
  };

  const handleResetPassword = (userId: string, email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: 'Reset Password',
      description: `Are you sure you want to send a password reset email to ${email}?`,
      confirmLabel: 'Send Reset Email',
      action: 'resetPassword',
      onConfirm: () => {
        sendPasswordReset(userId, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleToggleStatus = (
    userId: string,
    email: string,
    currentStatus: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    const isActive = currentStatus === 'active';
    setConfirmDialog({
      open: true,
      title: isActive ? 'Deactivate User' : 'Activate User',
      description: isActive
        ? `Are you sure you want to deactivate ${email}? The user will no longer be able to access the platform.`
        : `Are you sure you want to activate ${email}?`,
      confirmLabel: isActive ? 'Deactivate' : 'Activate',
      variant: isActive ? 'destructive' : 'default',
      action: 'toggleStatus',
      onConfirm: () => {
        const newStatus = isActive ? 'deactivated' : 'active';
        updateStatus(
          { id: userId, payload: { status: newStatus as 'active' | 'deactivated' } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
    });
  };

  const handleReset2FA = (userId: string, email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: 'Reset Two-Factor Authentication',
      description: `Are you sure you want to reset 2FA for ${email}? The user will need to set up 2FA again on their next login.`,
      confirmLabel: 'Reset 2FA',
      action: 'reset2fa',
      onConfirm: () => {
        resetTwoFactor(userId, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="active">Active</Badge>;
      case 'pending':
        return <Badge variant="pending">Pending</Badge>;
      case 'deactivated':
        return <Badge variant="error">Deactivated</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getUserDisplayName = (user: any) => {
    // Backend returns `name` (first name) and `last_name` separately
    const fullName = [user.name, user.last_name || user.lastName].filter(Boolean).join(' ');
    return fullName || user.email;
  };

  const getUserInitials = (user: any) => {
    const displayName = getUserDisplayName(user);
    const parts = displayName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  const totalPages = data ? data.totalPages : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Users</h1>
        <p className="text-muted-foreground">Manage all platform users</p>
      </div>

      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        {/* Search and Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or ID..."
              value={state.search || ''}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={state.status || 'all'}
            onValueChange={(value) => setFilter('status', value === 'all' ? undefined : value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={state.emailVerified || 'all'}
            onValueChange={(value) =>
              setFilter('emailVerified', value === 'all' ? undefined : value)
            }
            disabled={state.status === 'active'}
          >
            <SelectTrigger
              className="w-[160px]"
              title={
                state.status === 'active' ? 'Active users always have verified emails' : undefined
              }
            >
              <SelectValue placeholder="Email verified" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All emails</SelectItem>
              <SelectItem value="true">Verified</SelectItem>
              <SelectItem value="false">Unverified</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {isLoading ? (
          <TableSkeleton
            columns={5}
            rows={10}
            headers={['Name', 'Email', 'Organizations', 'Created', 'Actions']}
          />
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-red-600 mb-4">Failed to load data. Please try again.</p>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="text-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">No users found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {state.search || state.status || state.emailVerified
                ? 'Try adjusting your search or filter criteria'
                : 'No users have been created yet'}
            </p>
          </div>
        ) : (
          <>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      sortKey="name"
                      currentSortBy={state.sortBy}
                      currentSortOrder={state.sortOrder as SortOrder}
                      onSort={handleSort}
                    >
                      Name
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="email"
                      currentSortBy={state.sortBy}
                      currentSortOrder={state.sortOrder as SortOrder}
                      onSort={handleSort}
                    >
                      Email
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="organizationCount"
                      currentSortBy={state.sortBy}
                      currentSortOrder={state.sortOrder as SortOrder}
                      onSort={handleSort}
                    >
                      Organizations
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="createdAt"
                      currentSortBy={state.sortBy}
                      currentSortOrder={state.sortOrder as SortOrder}
                      onSort={handleSort}
                    >
                      Created
                    </SortableTableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((user) => (
                    <TableRow
                      key={user._id}
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleRowClick(user._id)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                              {getUserInitials(user)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-foreground">
                              {getUserDisplayName(user)}
                            </div>
                            {getStatusBadge(user.status)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.organizationCount || 0}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(user.createdAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(user._id);
                              }}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(user._id);
                              }}
                            >
                              <Copy className="mr-2 h-4 w-4" />
                              Copy ID
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => handleResetPassword(user._id, user.email, e)}
                            >
                              <KeyRound className="mr-2 h-4 w-4" />
                              Reset password
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => handleReset2FA(user._id, user.email, e)}
                            >
                              <ShieldOff className="mr-2 h-4 w-4" />
                              Reset 2FA
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.status === 'active' ? (
                              <DropdownMenuItem
                                onClick={(e) =>
                                  handleToggleStatus(user._id, user.email, user.status, e)
                                }
                                className="text-red-600 focus:text-red-600"
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={(e) =>
                                  handleToggleStatus(user._id, user.email, user.status, e)
                                }
                                className="text-green-600 focus:text-green-600"
                              >
                                <UserCheck className="mr-2 h-4 w-4" />
                                Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {totalPages > 1 ? (
                  <>
                    Showing {(state.page - 1) * state.limit + 1} to{' '}
                    {Math.min(state.page * state.limit, data.total)} of {data.total} users
                  </>
                ) : (
                  <>
                    Showing {data.total} {data.total === 1 ? 'user' : 'users'}
                  </>
                )}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(state.page - 1)}
                    disabled={state.page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {state.page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(state.page + 1)}
                    disabled={state.page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={
          confirmDialog.action === 'resetPassword'
            ? isSendingReset
            : confirmDialog.action === 'toggleStatus'
              ? isUpdatingStatus
              : confirmDialog.action === 'reset2fa'
                ? isResetting2FA
                : false
        }
      />
    </div>
  );
}
